import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Video, Heart, Brain, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const MagherafeltChronicPain: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Chronic Pain Treatment Magherafelt | Fibromyalgia Hypnotherapy Mid Ulster</title>
        <meta
          name="description"
          content="NHS-complementary chronic pain hypnotherapy serving Magherafelt. Online sessions recommended (no travel). Neuroscience approach for farming injuries, fibromyalgia, back pain. 15 years nursing experience."
        />
        <meta name="keywords" content="chronic pain treatment Magherafelt, fibromyalgia hypnotherapy Magherafelt, farming injury pain Mid Ulster, back pain treatment County Londonderry, online pain management" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/magherafelt-chronic-pain/" />
        <meta property="og:title" content="Chronic Pain Treatment Magherafelt | Fibromyalgia Hypnotherapy Mid Ulster" />
        <meta property="og:description" content="NHS-complementary chronic pain hypnotherapy serving Magherafelt. Online sessions - professional pain management without the drive." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Chronic Pain Treatment Magherafelt",
            "description": "CNHC registered hypnotherapy for chronic pain management, serving Magherafelt and Mid Ulster via online sessions and Belfast clinic. Specializing in farming injury pain, fibromyalgia, chronic back pain, arthritis, and work-related pain with NHS-complementary approach. Online sessions recommended for rural accessibility and privacy.",
            "url": "https://www.sharonclare-hypnotherapy.com/magherafelt-chronic-pain/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Chronic Pain Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Magherafelt",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "Northern Ireland"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "Belfast",
                  "postalCode": "BT7 2FZ",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How can Magherafelt residents access chronic pain treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The Belfast clinic is on the Ormeau Road in south Belfast, about 10 minutes from the city centre - reached from Magherafelt via the A29 and the M2. It is a long drive, so for most Magherafelt residents online video sessions are the most practical option, providing professional chronic pain treatment from your home without the round trip. Online sessions offer the same evidence-based hypnotherapy with complete convenience and privacy."
                }
              },
              {
                "@type": "Question",
                "name": "Can pain treatment be done online?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely - and online sessions are recommended for Magherafelt residents. Secure video hypnotherapy sessions provide the same professional, evidence-based chronic pain treatment as in-person sessions. Online eliminates travel time on rural roads, provides complete privacy in a small town where people know each other, and makes specialist pain management genuinely accessible for farming families and rural residents with demanding schedules. Many Magherafelt clients find online sessions more practical and prefer the convenience."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work for farming injuries?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is highly effective for chronic pain from farming injuries. Agricultural work creates back injuries from lifting, joint pain from repetitive movements, arthritis from years of physical labor, and shoulder/neck pain from machinery operation. When you combine physical injury with the stress of farming - financial uncertainty, weather concerns, isolation during busy seasons - your nervous system amplifies pain signals significantly. Hypnotherapy calms this overactive alarm system, reduces pain perception, and helps your brain process pain differently. Many farming community clients experience meaningful pain reduction and improved ability to continue working."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with agricultural work pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy addresses both the physical pain from agricultural work and the stress that worsens it. Farming creates chronic strain from heavy lifting, repetitive movements, awkward postures, and long hours. But agricultural stress - financial worries, weather uncertainty, market forces, physical isolation - keeps your nervous system on high alert, which amplifies pain. For Magherafelt farming community members, hypnotherapy provides tools to manage pain flare-ups during busy seasons, reduce overall pain levels, and address the stress-pain connection. This works alongside your medical care to help you maintain your livelihood."
                }
              },
              {
                "@type": "Question",
                "name": "How does rural healthcare access affect pain management?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Rural areas like Mid Ulster face significant healthcare access challenges - longer distances to specialists, fewer local services, and waiting times that can exceed 12-18 months for pain clinic appointments. For Magherafelt residents dealing with chronic pain, this means suffering without adequate support for extended periods. Online hypnotherapy provides a bridge - professional, evidence-based pain management you can access immediately from home, without traveling 40+ miles to Belfast or waiting months for NHS appointments. This works alongside your GP care to provide support while you wait for specialist treatment."
                }
              },
              {
                "@type": "Question",
                "name": "Can I continue NHS treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. Hypnotherapy is complementary to NHS care, not a replacement. I work collaboratively with your GP and medical team. With 15 years nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare providers. Many Magherafelt clients use hypnotherapy alongside physiotherapy, medication, and other NHS treatments. Never stop or change medications without consulting your GP - hypnotherapy enhances your medical care, it doesn't replace it."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Management for Magherafelt
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Neuroscience-based hypnotherapy serving Mid Ulster farming community
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-3">
                <MapPin className="w-6 h-6 text-accent-600" />
                <p className="text-lg font-semibold text-gray-900">
                  In person at the Belfast clinic on the Ormeau Road or the Lisburn clinic at The Arzen Collective
                </p>
              </div>
              <div className="flex items-center justify-center space-x-3 text-gray-600">
                <Video className="w-5 h-5 text-gray-500" />
                <p className="text-base">
                  Online sessions also available any day — clinic days and times vary
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request 10-Minute Call
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Hypnotherapy for Magherafelt
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Serving Magherafelt and Mid Ulster from our Belfast clinic, located approximately 30 miles northwest via the A29. However, I want to be upfront about distance: the journey takes 40-45 minutes by car through rural roads. For most Magherafelt residents dealing with persistent pain conditions like farming injury pain, fibromyalgia, chronic back pain, or arthritis, <strong>online video sessions are the most practical option</strong> - providing professional, evidence-based hypnotherapy support from your home without the 80-90 minute round trip.
              </p>
              <p>
                Living with chronic pain in Magherafelt's farming community comes with specific pressures that urban pain management services often don't understand. This is a traditional market town with deep agricultural heritage, where many residents work in farming, agricultural services, or small businesses serving rural communities. Agricultural work creates particular pain patterns - chronic back injuries from lifting heavy loads, joint pain and arthritis from years of repetitive movements, shoulder and neck pain from machinery operation, and the accumulated physical toll of outdoor work in all weathers.
              </p>
              <p>
                But it's not just the physical strain that affects chronic pain. Farming life involves unique stresses that significantly worsen pain: financial uncertainty tied to weather and market forces beyond your control, physical isolation during busy agricultural seasons, the mental load of being self-employed with no sick days or guaranteed income, and the accumulated pressure of multiple responsibilities. Modern pain neuroscience shows us clearly that stress directly amplifies pain signals. When your nervous system is on high alert from financial worries or weather stress, your brain turns up the volume on pain.
              </p>
              <p>
                This is where solution-focused hypnotherapy excels for Magherafelt's farming community. By calming your overactive nervous system, teaching your brain healthier pain processing patterns, and addressing the agricultural stress that intensifies pain, many clients experience significant reductions in pain levels and improvements in ability to work. And crucially, online sessions make this specialist support genuinely accessible - professional hypnotherapy from your home, without the travel burden that rural residents often face when seeking healthcare.
              </p>
              <p>
                With 15 years of nursing experience at Belfast City Hospital, I bring medical understanding to chronic pain treatment. I understand conditions like chronic back pain, fibromyalgia, and arthritis from both a clinical and therapeutic perspective. I know how frustrating it is when you're told to "rest" when you have lambing season or harvest, or when medications help a bit but don't address why stress makes everything worse. Hypnotherapy offers a science-backed approach that works alongside your medical care to help you manage pain better, continue working, and improve quality of life. This isn't about ignoring the pain - it's about retraining your nervous system to respond differently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Chronic Pain */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              The Neuroscience of Chronic Pain
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Persistent pain lasting beyond 12 weeks isn't "all in your head," but it is in your nervous system. Understanding how pain works helps explain why hypnotherapy can be so effective.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Shield className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Central Sensitization</h3>
                <p className="text-gray-700">
                  The spinal cord and brain become hypersensitive, amplifying normal signals and interpreting them as pain. This is why chronic pain can feel disproportionate to actual tissue damage, and why agricultural stress or financial worries can worsen pain intensity. Your nervous system's alarm is stuck on high volume.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Heart className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Fight-Flight-Freeze</h3>
                <p className="text-gray-700">
                  Ongoing stress from farming - financial uncertainty, weather worries, market forces, physical isolation, self-employment pressures - keeps your survival system on high alert. When your brain perceives threat, even from bills or weather forecasts, it keeps pain signals amplified as a protective mechanism. Calming this system is essential for pain reduction.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Brain className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Neuroplasticity</h3>
                <p className="text-gray-700">
                  The brain learns pain pathways through repetition. The more they're used, the easier they fire - like a well-worn farm track. But neuroplasticity also means we can create new, healthier pathways. Your brain can learn to process pain differently, and hypnotherapy facilitates that relearning.
                </p>
              </div>
            </div>
            <div className="mt-8 bg-white rounded-2xl p-8">
              <p className="text-gray-700 text-lg">
                This is why you can feel real, distressing pain even when medical scans look "normal." The pain is genuine - your brain is simply protecting you a little too well. For Magherafelt farming community members dealing with the physical demands of agricultural work combined with financial and weather stress, this overprotective system often gets stuck in overdrive. Hypnotherapy works by calming this overactive alarm system and helping your brain interpret sensations differently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              How Hypnotherapy Supports Pain Recovery
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center">
              Clinical hypnotherapy works directly with the mind-body connection to calm your overactive alarm system
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Heart className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Shift from Danger to Safety</h3>
                    <p className="text-gray-700">
                      Calms the fight-flight response so your nervous system stops amplifying pain signals. This is particularly important for Magherafelt farming community members dealing with the physical demands of agricultural work, financial uncertainty, weather stress, and the accumulated pressure of self-employed rural life.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Close the Pain Gate</h3>
                    <p className="text-gray-700">
                      Lowers the intensity of pain messages traveling through the spinal cord to the brain, reducing pain perception through descending pain inhibition pathways. Neuroscience research shows the brain can literally turn down the volume on pain signals when it feels safe.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Harness Neuroplasticity</h3>
                    <p className="text-gray-700">
                      Creates new, healthier neural pathways so your brain can learn to interpret sensations differently. The brain's remarkable ability to change means chronic pain doesn't have to be permanent. We can retrain your nervous system to respond to pain signals in a healthier way.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <CheckCircle className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Practical Self-Management Tools</h3>
                    <p className="text-gray-700">
                      Self-hypnosis and relaxation techniques you can use during farm work, at home, or during quiet moments to manage pain flare-ups, reduce stress, and improve sleep - all factors that significantly influence pain levels. These are practical tools for farming life, not just the therapy room.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <p className="text-gray-700 text-lg mb-4">
                Research supports hypnotherapy's role in multidisciplinary pain care. Studies show hypnotherapy can significantly reduce pain intensity for conditions like chronic back pain from agricultural work, fibromyalgia, and arthritis by working with the brain's natural pain control mechanisms. A major review found hypnotherapy 68% more effective than no treatment for chronic pain conditions.
              </p>
              <p className="text-gray-700 text-lg">
                For Magherafelt residents facing rural healthcare access challenges - long distances to specialists, extended NHS waiting times (often 12-18 months for pain clinic appointments) - online hypnotherapy provides a practical bridge. Professional, evidence-based pain management you can access immediately from home, without a long round trip into Belfast and back. This works alongside your GP care to provide support while you wait for specialist treatment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sharon's Approach */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Working Alongside Your Medical Care
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Hypnotherapy is complementary to NHS care, not a replacement for medical treatment. With Northern Trust and Western Trust pain clinic waiting times often exceeding 12-18 months, many Magherafelt residents need support now while waiting for specialist appointments. Hypnotherapy can provide that bridge, offering relief and improved functioning while you wait for or work alongside NHS care.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                I work collaboratively with your GP, pain specialists, physiotherapists, and other healthcare providers. Many chronic pain clinics now recommend hypnotherapy as part of a multidisciplinary approach alongside physiotherapy, medication, mindfulness, or gentle movement programs. By addressing the nervous system directly, hypnotherapy enhances these treatments and helps you maintain your livelihood, mobility, and quality of life despite pain.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With 15 years of nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare team. I know the medications, the procedures, the specialist terminology, and the frustrations of chronic pain from both sides of the clinical encounter. This medical background means I understand when something needs medical attention versus when hypnotherapy can help, and I'll always support you in getting appropriate medical care.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Medical Background</h3>
                <p className="text-gray-700">15 years Belfast City Hospital nursing experience brings clinical credibility to pain management</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">CNHC Registered</h3>
                <p className="text-gray-700">Professional registration ensures you receive evidence-based, ethical treatment</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">GP Collaboration</h3>
                <p className="text-gray-700">I work with your GP and medical team, not in competition with them</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Evidence-Based Approach</h3>
                <p className="text-gray-700">Neuroscience-backed techniques supported by research, not pseudoscience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There / Online Sessions */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Access Options from Magherafelt
            </h2>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Video className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">Online Sessions (Recommended)</h3>
                  <p className="text-gray-700 mb-4">
                    Secure video hypnotherapy sessions provide professional, evidence-based chronic pain treatment from the comfort and privacy of your own home in Magherafelt. Online sessions offer:
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>No travel time</strong> - eliminates 80-90 minute round trip on rural roads</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Complete privacy</strong> - confidential support outside close-knit small-town networks</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Flexible scheduling</strong> - fits around farming demands and busy agricultural seasons</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Same effectiveness</strong> - research shows online hypnotherapy equals in-person for pain management</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <div className="flex items-start space-x-4">
                <Car className="w-8 h-8 text-gray-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">In-Person at Belfast Clinic (If Preferred)</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Address:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> Approximately 30 miles
                  </p>
                  <p className="text-gray-700 mb-4">
                    <span className="font-semibold">Time:</span> 40-45 minutes in normal conditions
                  </p>
                  <p className="text-gray-700">
                    While in-person sessions are available if you prefer face-to-face treatment, the distance means most Magherafelt residents find online sessions more practical. Time is precious in farming and rural business life, and online sessions respect that reality while providing the same professional pain management support.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Mid Ulster Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "My chronic back pain from years of farming was affecting everything - my ability to work, my sleep, my mood. The online hypnotherapy sessions with Sharon were perfect for our schedule - no driving into Belfast during busy seasons. She helped me understand how financial stress and weather worries were making the pain worse. After 7 sessions, my pain levels are much more manageable and I've learned techniques I can use during farm work. Her nursing background meant she understood the physical demands and the medical side."
                </p>
                <p className="font-semibold text-gray-900">J.D., Mid Ulster - Farming Injury Chronic Back Pain</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working in agricultural services, my shoulder and joint pain was getting worse every year. I couldn't afford to stop working but the pain was becoming unbearable. Sharon's online approach was brilliant - professional treatment from home, complete privacy, and it actually worked. My pain intensity has dropped significantly and I'm managing my work demands better. The convenience of online sessions made it sustainable when traveling to clinics would have been impossible to maintain."
                </p>
                <p className="font-semibold text-gray-900">M.K., County Londonderry - Repetitive Strain Agricultural Work</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Living with fibromyalgia in a rural area, I'd struggled for years with limited local support. The online hypnotherapy with Sharon has been life-changing. She helped me manage my pain in ways the NHS couldn't offer during the long waiting times. I'm sleeping better, my flare-ups are less severe, and I feel like I have some control back. The privacy of online sessions was important to me in a small town where everyone knows everyone."
                </p>
                <p className="font-semibold text-gray-900">S.W., Mid Ulster - Fibromyalgia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Magherafelt Chronic Pain Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How can Magherafelt residents access chronic pain treatment?
                </h3>
                <p className="text-gray-700">
                  The Belfast clinic is on the Ormeau Road in south Belfast, about 10 minutes from the city centre - reached from Magherafelt via the A29 and the M2. It is a long drive, so for most Magherafelt residents online video sessions are the most practical option, providing professional chronic pain treatment from your home without the round trip. Online sessions offer the same evidence-based hypnotherapy with complete convenience and privacy.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can pain treatment be done online?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely - and online sessions are recommended for Magherafelt residents. Secure video hypnotherapy sessions provide the same professional, evidence-based chronic pain treatment as in-person sessions. Online eliminates travel time on rural roads, provides complete privacy in a small town where people know each other, and makes specialist pain management genuinely accessible for farming families and rural residents with demanding schedules. Many Magherafelt clients find online sessions more practical and prefer the convenience.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work for farming injuries?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is highly effective for chronic pain from farming injuries. Agricultural work creates back injuries from lifting, joint pain from repetitive movements, arthritis from years of physical labor, and shoulder/neck pain from machinery operation. When you combine physical injury with the stress of farming - financial uncertainty, weather concerns, isolation during busy seasons - your nervous system amplifies pain signals significantly. Hypnotherapy calms this overactive alarm system, reduces pain perception, and helps your brain process pain differently. Many farming community clients experience meaningful pain reduction and improved ability to continue working.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with agricultural work pain?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy addresses both the physical pain from agricultural work and the stress that worsens it. Farming creates chronic strain from heavy lifting, repetitive movements, awkward postures, and long hours. But agricultural stress - financial worries, weather uncertainty, market forces, physical isolation - keeps your nervous system on high alert, which amplifies pain. For Magherafelt farming community members, hypnotherapy provides tools to manage pain flare-ups during busy seasons, reduce overall pain levels, and address the stress-pain connection. This works alongside your medical care to help you maintain your livelihood.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does rural healthcare access affect pain management?
                </h3>
                <p className="text-gray-700">
                  Rural areas like Mid Ulster face significant healthcare access challenges - longer distances to specialists, fewer local services, and waiting times that can exceed 12-18 months for pain clinic appointments. For Magherafelt residents dealing with chronic pain, this means suffering without adequate support for extended periods. Online hypnotherapy provides a bridge - professional, evidence-based pain management you can access immediately from home, without traveling 40+ miles to Belfast or waiting months for NHS appointments. This works alongside your GP care to provide support while you wait for specialist treatment.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I continue NHS treatment?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. Hypnotherapy is complementary to NHS care, not a replacement. I work collaboratively with your GP and medical team. With 15 years nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare providers. Many Magherafelt clients use hypnotherapy alongside physiotherapy, medication, and other NHS treatments. Never stop or change medications without consulting your GP - hypnotherapy enhances your medical care, it doesn't replace it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Take the First Step Toward Pain Relief"
        subtitle="If you're ready to explore how clinical hypnotherapy can help with chronic pain, book your initial consultation today. Online sessions make specialist pain management accessible from your home in Magherafelt."
        primaryButtonText="Request 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default MagherafeltChronicPain;
